/**
 * @file 单币/多币赎回选择
 */
import { BoxedSelect } from '@bfe/moly';
import { UnstakeType, UnstakeCoinInfoStore } from '@/types/unstake';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import BigNumber from 'bignumber.js';
import { useFm } from '@web3/base-hooks';
import cls from 'classnames';
import { formatUSDYield } from '@/utils/num';

import styles from './index.module.scss';

const BoxedSelectGroup = BoxedSelect.Group;

interface Props {
  unstakeType: UnstakeType;
  selectCoinList: string[];
  unstakeCoinAmountStore: UnstakeCoinInfoStore;
  onChangeUnstakeType: (v: UnstakeType) => void;
  onChangeSelectCoinList: (v: string[]) => void;
  isSolLoss?: boolean;
}

export const SelectUnstakeType = (props: Props) => {
  const fm = useFm();
  const {
    unstakeType,
    selectCoinList,
    unstakeCoinAmountStore,
    isSolLoss = false,
    onChangeUnstakeType,
    onChangeSelectCoinList,
  } = props;
  const { poolInfo } = usePoolInfoContext();
  const { coinInfoStore } = useCoinInfoContext();

  return (
    <div className={styles.container}>
      {/* 单币和多币赎回类型都有的情况下才会展示Tab */}
      {poolInfo.singleCoins && poolInfo.multipleCoins && (
        <div className={styles.switchTabs}>
          <div
            className={cls(styles.tab, {
              [styles.activeTab]: unstakeType === UnstakeType.Single,
            })}
            onClick={() => onChangeUnstakeType(UnstakeType.Single)}
          >
            {fm('single_coin')}
          </div>
          <div
            className={cls(styles.tab, {
              [styles.activeTab]: unstakeType === UnstakeType.Multiple,
            })}
            onClick={() => onChangeUnstakeType(UnstakeType.Multiple)}
          >
            {fm('multiple_coins')}
          </div>
        </div>
      )}
      <div className={styles.receiveTitle}>{fm('received')}</div>
      <div className={styles.receiveContent}>
        {unstakeType === UnstakeType.Single && (
          <BoxedSelectGroup
            value={selectCoinList[0]}
            onChange={(v) => {
              if (v) {
                onChangeSelectCoinList(Array.isArray(v) ? v : [v]);
              }
            }}
          >
            {poolInfo.singleCoins.map((item) => {
              const coinInfo =
                coinInfoStore[`${poolInfo.chainCode}${item.contractAddress}`];
              const { amount, slippage } =
                unstakeCoinAmountStore[item.contractAddress] || {};
              return (
                <BoxedSelect
                  value={item.contractAddress}
                  key={item.contractAddress}
                  className={cls(
                    styles.boxedSelect,
                    item.contractAddress === selectCoinList[0]
                      ? styles.selected
                      : ''
                  )}
                >
                  <div className={styles.singleCoinRow}>
                    <div className={styles.singleCoinRowLeft}>
                      <img
                        className={styles.singleCoinIcon}
                        src={coinInfo.iconNightUrl}
                      />
                      <div className={styles.singleCoinSymbol}>
                        {coinInfo.symbol}
                      </div>
                      {isSolLoss || BigNumber(slippage).gte(1) ? (
                        <div className={styles.loss}>{fm('damage')}</div>
                      ) : null}
                    </div>
                    <div>
                      <div className={styles.singleCoinAmount}>
                        {amount ? amount : '--'}
                      </div>
                      <div className={styles.singleCoinValue}>
                        {coinInfo.price && amount
                          ? formatUSDYield(
                              BigNumber(coinInfo.price).times(amount).toString()
                            )
                          : '--'}
                      </div>
                    </div>
                  </div>
                </BoxedSelect>
              );
            })}
          </BoxedSelectGroup>
        )}
        {unstakeType === UnstakeType.Multiple && (
          <div className={styles.multipleCoins}>
            {poolInfo.multipleCoins.map((item, index) => {
              const coinInfo =
                coinInfoStore[`${poolInfo.chainCode}${item.contractAddress}`];
              const { amount } =
                unstakeCoinAmountStore[item.contractAddress] || {};
              return (
                <div className={styles.multipleCoinsRow} key={index}>
                  <div className={styles.singleCoinRowLeft}>
                    <img
                      className={styles.singleCoinIcon}
                      src={coinInfo.iconNightUrl}
                    />
                    <div className={styles.singleCoinSymbol}>
                      {coinInfo.symbol}
                    </div>
                  </div>
                  <div>
                    <div className={styles.singleCoinAmount}>
                      {amount ? amount : '--'}
                    </div>
                    <div className={styles.singleCoinValue}>
                      {coinInfo.price && amount
                        ? formatUSDYield(
                            BigNumber(coinInfo.price).times(amount).toString()
                          )
                        : '--'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className={styles.line}></div>
    </div>
  );
};
